import { ReactNode } from 'react'

/**
 * La cote — signature visuelle du site.
 *
 * Un filet terminé par deux traits d'attache, portant une étiquette
 * monospace. L'étiquette porte toujours une donnée réelle tirée du
 * contenu (délai, tarif, code postal, nombre de corps d'état) : jamais
 * une numérotation décorative 01/02/03.
 */
export function Cote({
  label,
  className = '',
  tone = 'dark',
}: {
  label: string
  className?: string
  tone?: 'dark' | 'light' | 'signal'
}) {
  const toneClass = {
    dark: 'text-anthracite',
    light: 'text-mist',
    // signal-deep et non signal : l'orange vif ne fait que 3,4:1 sur
    // fond clair, sous le seuil AA pour du texte de 11 px.
    signal: 'text-signal-deep',
  }[tone]

  return (
    <div
      className={`flex items-center gap-3 ${toneClass} ${className}`}
      aria-hidden="true"
    >
      <span className="annot whitespace-nowrap">{label}</span>
      <span className="rule-tick-caps flex-1" />
    </div>
  )
}

/**
 * En-tête de section : cote + titre + chapô éventuel.
 * L'alignement est à gauche par défaut — le centrage systématique est
 * ce qui donne aux sites d'artisans leur air de gabarit.
 */
export function SectionHead({
  cote,
  title,
  children,
  tone = 'dark',
  className = '',
  as: Heading = 'h2',
  id,
}: {
  cote: string
  title: ReactNode
  children?: ReactNode
  tone?: 'dark' | 'light'
  className?: string
  as?: 'h1' | 'h2'
  id?: string
}) {
  const isLight = tone === 'light'

  return (
    <div className={`max-w-3xl ${className}`}>
      <Cote label={cote} tone={isLight ? 'light' : 'dark'} className="mb-6" />
      <Heading id={id} className={isLight ? 'text-chalk' : 'text-ink'}>
        {title}
      </Heading>
      {children ? (
        <div
          className={`mt-6 text-base tablet:text-lg ${
            isLight ? '[&_p]:text-mist' : '[&_p]:text-anthracite'
          }`}
        >
          {children}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Étiquette de donnée : un chiffre du contenu mis en avant avec sa
 * légende, en monospace. Utilisé pour les délais et tarifs publiés.
 */
export function Figure({
  value,
  label,
  tone = 'dark',
}: {
  value: string
  label: string
  tone?: 'dark' | 'light'
}) {
  const isLight = tone === 'light'

  return (
    <div className="flex flex-col gap-2">
      <span
        data-figure
        className={`font-display text-3xl tablet:text-4xl font-bold tracking-tight ${
          isLight ? 'text-chalk' : 'text-ink'
        }`}
      >
        {value}
      </span>
      <span className={`annot ${isLight ? 'text-mist' : 'text-graphite'}`}>
        {label}
      </span>
    </div>
  )
}
